// Nomad-mon Go! — ETERNAL WISH + PITY METER (the client face of whatsapp-poster
// src/routes/nomadmon-eternal.ts; the rules live in packdraw.js, the state in identity.js).
//
//   meter   a thin ring on the pack screen filling toward the hard pity (50), the wished
//           Eternal's face inside it. Sealed pack: this pack's n. Summary: packs since the
//           last Eternal. Tapping it opens the picker.
//   picker  the 24 Eternals; unowned ones are silhouettes with their name. Tap selects (with a
//           sound), the primary button makes the wish (once a UTC day).
//   stamp   WISH GRANTED: the extra beat on a god pull that IS the wish (pack.js godAftermath).
//
// Reading text is DOM; keyframes animate transform/opacity only (theme-v2 house rules).
import * as audio from './audio.js';
import * as identity from './identity.js';
import * as haptics from './haptics.js';
import { artSrc } from './types.js';
import { displayName } from './names.js';

let K = null;                      // { deck, V, toast }
let meterEl = null, stampEl = null, pickerEl = null;
let meterMode = null;              // 'sealed' | 'summary' | null (hidden)
const PICK_SFX = ['sheet', 'cxchime', 'cxflourish', 'deny', 'tick'];

export function initEternalWish(ctx) {
  K = ctx;
  identity.setEternalCatalogue(eternals().map(d => d.id));
}

function eternals() {
  if (!K) return [];
  return K.deck.filter(d => d.rarity === 'eternal' && !d.forged && !d.altOf)
    .sort((a, b) => a.name.localeCompare(b.name));
}
const byId = (id) => (K && id ? K.deck.find(d => d.id === id) : null) || null;
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const focal = (d) => {
  const x = (d.thumbFocal && d.thumbFocal.x) ?? d.focalX ?? 0.5, y = (d.thumbFocal && d.thumbFocal.y) ?? 0.36;
  return `${Math.round(x * 100)}% ${Math.round(y * 100)}%`;
};
function fmtWait(ts) {
  const ms = Math.max(0, (Number(ts) || 0) - Date.now());
  const h = Math.floor(ms / 3600e3), m = Math.max(1, Math.floor((ms % 3600e3) / 60e3));
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ---------------------------------------------------------------- the meter
const RING_R = 18.5, RING_C = 2 * Math.PI * RING_R;   // a 40px dial (viewBox 40)
function ensureMeter() {
  if (meterEl) return meterEl;
  const host = document.getElementById('packUI') || document.body;
  meterEl = document.createElement('button');
  meterEl.type = 'button';
  meterEl.id = 'eternalMeter';
  meterEl.innerHTML = `<span class="etm-dial"><svg class="etm-ring" viewBox="0 0 40 40" aria-hidden="true">
      <circle class="etm-track" cx="20" cy="20" r="${RING_R}"/><circle class="etm-soft" cx="20" cy="20" r="${RING_R}"/>
      <circle class="etm-fill" cx="20" cy="20" r="${RING_R}"/></svg>
    <span class="etm-face"><img alt="" decoding="async" draggable="false" hidden><i class="etm-plus" aria-hidden="true">+</i></span></span>
    <span class="etm-txt"><span class="etm-k">Eternal</span><span class="etm-n"><b></b><i></i></span></span>`;
  // the soft-pity arc: where the ramp begins (a quiet tick of warmer light, 30 -> 50)
  meterEl.addEventListener('pointerdown', (e) => e.stopPropagation());   // never a tear / grab on the pack
  meterEl.addEventListener('click', (e) => { e.stopPropagation(); openWishPicker({ from: 'pack' }); });
  host.appendChild(meterEl);
  return meterEl;
}
/** mode: 'sealed' (the pack in hand) | 'summary' (after the reveal) | null (hide). */
export function setMeter(mode) {
  meterMode = mode;
  const el = ensureMeter();
  if (!mode) { el.classList.remove('show'); return; }
  paintMeter();
  el.classList.add('show');
  if (identity.eternalState() == null) identity.fetchEternal().then(() => { if (meterMode) paintMeter(); });
}
function paintMeter() {
  const el = ensureMeter();
  const s = identity.eternalState();
  if (!s) { el.classList.add('pending'); return; }
  el.classList.remove('pending');
  const hard = s.hard || 50, soft = s.softFrom || 30;
  const n = meterMode === 'sealed' ? Math.max(1, identity.eternalLastPack().pack || s.since + 1) : s.since;
  const v = Math.max(0, Math.min(hard, n));
  const fill = el.querySelector('.etm-fill');
  fill.style.strokeDasharray = `${RING_C}`;
  fill.style.strokeDashoffset = `${RING_C * (1 - v / hard)}`;
  const softArc = el.querySelector('.etm-soft');
  softArc.style.strokeDasharray = `${RING_C * (1 - (soft - 1) / hard)} ${RING_C}`;
  softArc.style.strokeDashoffset = `${-RING_C * ((soft - 1) / hard)}`;
  el.classList.toggle('warm', v >= soft);
  el.classList.toggle('full', v >= hard);
  el.classList.toggle('sure', !!s.guaranteed && !!s.wish);
  el.querySelector('.etm-n b').textContent = String(v);
  el.querySelector('.etm-n i').textContent = `/${hard}`;
  const w = byId(s.wish);
  const img = el.querySelector('.etm-face img');
  if (w) {
    const src = artSrc(w, K.V, 'thumb');
    if (img.getAttribute('src') !== src) img.src = src;
    img.style.objectPosition = focal(w);
    img.hidden = false;
  } else img.hidden = true;
  el.classList.toggle('nowish', !w);
  el.setAttribute('aria-label', `Eternal: ${v} of ${hard} packs. ${w ? `Wish: ${displayName(w.name)}${s.guaranteed ? ', next Eternal' : ''}.` : 'No wish yet.'} Choose your wish`);
}

// ---------------------------------------------------------------- WISH GRANTED
/** The extra beat on a wished god pull (null hides it). montage: the Open-all flash (centred on its card). */
export function wishStamp(d, { montage = false, anchor = null } = {}) {
  const host = document.getElementById('packUI') || document.body;
  if (!stampEl) {
    stampEl = document.createElement('div');
    stampEl.id = 'wishStamp';
    stampEl.setAttribute('aria-live', 'polite');
    stampEl.innerHTML = '<span class="ws-ink"><span class="ws-main">Wish Granted</span></span>';
    host.appendChild(stampEl);
  }
  if (!d) { stampEl.classList.remove('show'); return; }
  stampEl.classList.remove('show');
  stampEl.classList.toggle('mont', !!montage);
  // anchored: centred on the revealed card's lower third and sized to it; else the CSS band under the engraving
  stampEl.classList.toggle('anchored', !!anchor);
  stampEl.style.left = anchor ? `${anchor.x.toFixed(1)}px` : '';
  stampEl.style.top = anchor ? `${anchor.y.toFixed(1)}px` : '';
  stampEl.style.setProperty('--ws-w', anchor && anchor.w ? `${Math.round(anchor.w)}px` : '');
  void stampEl.offsetWidth;
  stampEl.classList.add('show');
  audio.play('legendcrown', { gain: 0.95 });
  haptics.buzz([18, 40, 60]);
}
export const isWishGranted = (id) => identity.wishGranted(id);

// ---------------------------------------------------------------- the picker
function ensurePicker() {
  if (pickerEl) return pickerEl;
  pickerEl = document.createElement('div');
  pickerEl.id = 'wishPicker';
  pickerEl.setAttribute('role', 'dialog');
  pickerEl.setAttribute('aria-modal', 'true');
  pickerEl.setAttribute('aria-labelledby', 'wpTitle');
  pickerEl.innerHTML = `<div class="wp-scrim"></div>
    <div class="wp-sheet">
      <header class="wp-head">
        <div><h2 id="wpTitle">Choose your Eternal wish</h2><p class="wp-sub"></p></div>
        <button type="button" class="wp-x" aria-label="Close"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
      </header>
      <div class="wp-grid" role="listbox" aria-label="Eternals"></div>
      <footer class="wp-foot"><span class="wp-note" aria-live="polite"></span>
        <button type="button" class="v2-btn v2-btn--primary wp-ok">Make this my wish</button></footer>
    </div>`;
  document.body.appendChild(pickerEl);
  pickerEl.addEventListener('pointerdown', (e) => e.stopPropagation());
  pickerEl.addEventListener('click', (e) => {
    e.stopPropagation();
    if (e.target.closest('.wp-x') || e.target.classList.contains('wp-scrim')) { closeWishPicker(); return; }
    const c = e.target.closest('.wp-c');
    if (c) { select(c.dataset.id); return; }
    if (e.target.closest('.wp-ok')) commit();
  });
  pickerEl.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.stopPropagation(); closeWishPicker(); } });
  return pickerEl;
}
let pickSel = null, pickBusy = false, pickFrom = null;

export function wishPickerOpen() { return !!(pickerEl && pickerEl.classList.contains('open')); }

/** Open the picker (from: 'pack' | 'codex' | 'collection' — QA reads it on data-from). */
export async function openWishPicker({ from = 'pack' } = {}) {
  if (!K) return;
  const el = ensurePicker();
  pickFrom = from;
  el.dataset.from = from;
  audio.loadSfx(PICK_SFX).catch(() => {});
  audio.ui('sheet');
  const s0 = identity.eternalState();
  pickSel = (s0 && s0.wish) || null;
  buildGrid();
  paintPicker();
  el.classList.add('open');
  document.body.classList.add('wishpicking');
  setTimeout(() => { const f = el.querySelector('.wp-c.sel') || el.querySelector('.wp-c'); if (f) f.focus({ preventScroll: false }); }, 40);
  const s = await identity.fetchEternal();
  if (wishPickerOpen() && s) { if (!pickSel) pickSel = s.wish || null; buildGrid(); paintPicker(); }
}
export function closeWishPicker() {
  if (!wishPickerOpen()) return;
  pickerEl.classList.remove('open');
  document.body.classList.remove('wishpicking');
  audio.ui('sheet', { rate: 0.9 });
  if (meterMode) paintMeter();
}
function buildGrid() {
  const grid = pickerEl.querySelector('.wp-grid');
  const s = identity.eternalState() || {};
  grid.innerHTML = eternals().map((d) => {
    const owned = identity.ownedCount(d.id) > 0;
    const nm = esc(displayName(d.name));
    return `<button type="button" class="wp-c${owned ? ' owned' : ''}${d.id === s.wish ? ' wish' : ''}" role="option" data-id="${esc(d.id)}"
      aria-label="${nm}${owned ? '' : ', not yet owned'}${d.id === s.wish ? ', your wish' : ''}">
      <span class="wp-card"><span class="wp-art"><img alt="" loading="lazy" decoding="async" draggable="false" src="${esc(artSrc(d, K.V, 'thumb'))}" style="object-position:${focal(d)}"></span>
        <i class="wp-frame" aria-hidden="true"></i>${owned ? '<i class="wp-own" aria-hidden="true"><svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 8.5l3 3 6-7"/></svg></i>' : ''}
        <i class="wp-tag" aria-hidden="true">Wish</i></span>
      <span class="wp-nm">${nm}</span></button>`;
  }).join('');
}
function paintPicker() {
  const s = identity.eternalState() || { wish: null, guaranteed: false, canChangeWish: true };
  for (const c of pickerEl.querySelectorAll('.wp-c')) {
    const on = c.dataset.id === pickSel;
    c.classList.toggle('sel', on);
    c.setAttribute('aria-selected', on ? 'true' : 'false');
    c.classList.toggle('wish', c.dataset.id === s.wish);
  }
  const sub = pickerEl.querySelector('.wp-sub');
  sub.textContent = s.wish && s.guaranteed ? 'Your next Eternal is your wish.'
    : 'Every Eternal: even odds it is your wish. Miss once, and the next one is.';
  const ok = pickerEl.querySelector('.wp-ok');
  const note = pickerEl.querySelector('.wp-note');
  const same = !!pickSel && pickSel === s.wish;
  const locked = !!s.wish && s.canChangeWish === false;
  ok.disabled = !pickSel || same || locked || pickBusy;
  ok.textContent = same ? 'Your wish' : 'Make this my wish';
  const d = byId(pickSel);
  note.textContent = locked && !same ? `Your wish can change in ${fmtWait(s.nextWishChangeUtc)}`
    : d ? displayName(d.name) : 'Choose one Eternal';
  pickerEl.classList.toggle('locked', locked);
}
function select(id) {
  if (pickBusy) return;
  if (id === pickSel) { audio.ui('tick', { rate: 1.1 }); return; }
  pickSel = id;
  const s = identity.eternalState();
  if (s && s.wish && s.canChangeWish === false && id !== s.wish) audio.ui('deny');   // browsable, but today's wish is set
  else { audio.ui('cxchime'); haptics.tap(); }
  paintPicker();
}
async function commit() {
  if (pickBusy || !pickSel) return;
  pickBusy = true;
  paintPicker();
  const r = await identity.setEternalWish(pickSel);
  pickBusy = false;
  if (r.ok) {
    audio.ui('cxflourish');
    haptics.buzz([14, 30, 22]);
    pickerEl.classList.remove('made'); void pickerEl.offsetWidth; pickerEl.classList.add('made');
    paintPicker();
    if (meterMode) paintMeter();
    setTimeout(closeWishPicker, 650);
    return;
  }
  audio.ui('deny');
  if (r.code === 'WISH_LOCKED' && K.toast) K.toast(`Your wish can change once a day. Next change in ${fmtWait(r.nextWishChangeUtc)}.`);
  else if (K.toast) K.toast('The wish did not save. Check your connection and try again.');
  await identity.fetchEternal();
  paintPicker();
}

// QA hook (window.__nmWish): read-only state + the three surfaces, no economy side effects.
if (typeof window !== 'undefined') {
  window.__nmWish = {
    state: () => identity.eternalState(),
    last: () => identity.eternalLastPack(),
    open: (from) => openWishPicker({ from }),
    close: () => closeWishPicker(),
    meter: (mode) => setMeter(mode),
    // the stamp lands on the card being REVEALED (window.__nmPack.wishAnchor); with no revealed card it refuses
    stamp: (id) => {
      if (!id) { wishStamp(null); return true; }
      const P = window.__nmPack, anchor = P && P.wishAnchor ? P.wishAnchor() : null;
      if (!anchor) { console.warn('[wish] no revealed card: tear the pack and reveal a card first'); return false; }
      wishStamp(byId(id) || { id }, { anchor });
      return true;
    },
    storageKey: () => identity.eternalStorageKey(),
  };
}
